'use client';

import React, { useState, useEffect } from 'react';
import { FaCalculator, FaTag, FaMoneyBill, FaChartLine, FaUsers, FaPercentage, FaQuestionCircle, FaInfoCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { useTheme } from 'next-themes';
import { toast } from 'react-hot-toast';

const DoubleDiscountCalculator = () => {
  const [originalPrice, setOriginalPrice] = useState('');
  const [firstDiscount, setFirstDiscount] = useState('');
  const [secondDiscount, setSecondDiscount] = useState('');
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { theme } = useTheme();

  const calculateDiscount = () => {
    setError('');
    setLoading(true);

    const op = parseFloat(originalPrice);
    const fd = parseFloat(firstDiscount);
    const sd = parseFloat(secondDiscount);

    if (!op || !fd || !sd) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (fd < 0 || fd > 100 || sd < 0 || sd > 100) {
      setError('Discount percentages must be between 0 and 100');
      setLoading(false);
      return;
    }

    // Calculate intermediate price after first discount
    const intermediatePrice = op * (1 - fd / 100);
    // Calculate final price after second discount
    const finalPrice = intermediatePrice * (1 - sd / 100);
    
    // Calculate total discount percentage
    const totalDiscount = ((op - finalPrice) / op) * 100;
    
    // Calculate savings
    const savings = op - finalPrice;

    const resultData = {
      intermediatePrice: intermediatePrice.toFixed(2),
      finalPrice: finalPrice.toFixed(2),
      totalDiscount: totalDiscount.toFixed(2),
      savings: savings.toFixed(2)
    };

    // Update chart data
    setChartData({
      series: [
        {
          name: 'Price',
          data: [op, intermediatePrice, finalPrice]
        }
      ],
      options: {
        chart: {
          type: 'line',
          height: 350,
          sparkline: {
            enabled: true
          }
        },
        colors: ['#4F46E5'],
        stroke: {
          width: 2
        },
        markers: {
          size: 6,
          colors: ['#4F46E5'],
          strokeWidth: 3
        },
        xaxis: {
          categories: ['Original Price', 'After 1st Discount', 'Final Price'],
          labels: {
            style: {
              colors: theme === 'dark' ? '#fff' : '#333'
            }
          }
        },
        yaxis: {
          labels: {
            formatter: function (val) {
              return '₹' + val.toFixed(2);
            },
            style: {
              colors: theme === 'dark' ? '#fff' : '#333'
            }
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return '₹' + val.toFixed(2);
            }
          }
        }
      }
    });

    setResult(resultData);
    setLoading(false);
  };

  const resetCalculator = () => {
    setOriginalPrice('');
    setFirstDiscount('');
    setSecondDiscount('');
    setResult(null);
    setChartData({});
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>Double Discount Calculator - Calculate Sequential Discounts</title>
        <meta name="description" content="Calculate double discounts with our advanced calculator. Get accurate results for sequential discounts and visualize price changes with our interactive chart." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="calculator-header text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="black dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-2xl mx-auto backdrop-blur-sm"
          >
            <div className="flex items-center justify-center mb-6">
              <FaCalculator className="text-4xl text-purple-500 dark:text-purple-400" />
              <h2 className="text-2xl font-bold ml-3">Double Discount Calculator</h2>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg mb-4 backdrop-blur-sm">
                <p className="text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2 backdrop-blur-sm">
                  <FaMoneyBill className="text-green-500 dark:text-green-400" />
                  <input
                    type="number"
                    placeholder="Original Price (₹)"
                    value={originalPrice}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setOriginalPrice(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2 backdrop-blur-sm">
                  <FaPercentage className="text-yellow-500 dark:text-yellow-400" />
                  <input
                    type="number"
                    placeholder="First Discount (%)"
                    value={firstDiscount}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setFirstDiscount(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2 backdrop-blur-sm">
                  <FaPercentage className="text-yellow-500 dark:text-yellow-400" />
                  <input
                    type="number"
                    placeholder="Second Discount (%)"
                    value={secondDiscount}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setSecondDiscount(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={calculateDiscount}
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-green-500 py-3 rounded-lg hover:from-purple-600 hover:to-pink-600 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Calculating...
                  </div>
                ) : 'Calculate Discount'}
              </button>

              <button
                type="button"
                onClick={resetCalculator}
                className="w-full bg-gradient-to-r from-gray-500 to-gray-700 text-white py-3 rounded-lg hover:from-gray-600 hover:to-gray-800 transition duration-300"
              >
                Reset Calculator
              </button>
            </div>

            {result && (
              <div className="space-y-6 mt-8">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg backdrop-blur-sm">
                  <h3 className="text-xl font-semibold mb-4">Calculation Results</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                      <h4 className="text-lg font-semibold mb-2">Original Price</h4>
                      <p className="text-2xl">₹{result.intermediatePrice}</p>
                    </div>
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                      <h4 className="text-lg font-semibold mb-2">After First Discount</h4>
                      <p className="text-2xl">₹{result.finalPrice}</p>
                    </div>
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                      <h4 className="text-lg font-semibold mb-2">Final Price</h4>
                      <p className="text-2xl">₹{result.finalPrice}</p>
                    </div>
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                      <h4 className="text-lg font-semibold mb-2">Total Discount</h4>
                      <p className="text-2xl">{result.totalDiscount}%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg backdrop-blur-sm">
                  <h3 className="text-xl font-semibold mb-4">Price Comparison Chart</h3>
                  <Chart
                    options={chartData.options}
                    series={chartData.series}
                    type="line"
                    height={350}
                  />
                </div>
              </div>
            )}
          </motion.div>
        </div>

        <div className="mt-12">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-3xl mx-auto backdrop-blur-sm">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-4">How to Use</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <FaInfoCircle className="text-purple-500 dark:text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Enter Original Price</h3>
                      <p className="text-gray-600 dark:text-gray-400">Input the original price of the product before any discounts.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <FaTag className="text-yellow-500 dark:text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">First Discount</h3>
                      <p className="text-gray-600 dark:text-gray-400">Enter the first discount percentage (0-100%).</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <FaTag className="text-yellow-500 dark:text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Second Discount</h3>
                      <p className="text-gray-600 dark:text-gray-400">Enter the second discount percentage (0-100%) that will be applied to the intermediate price.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <FaCalculator className="text-purple-500 dark:text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Calculate</h3>
                      <p className="text-gray-600 dark:text-gray-400">Click the "Calculate Discount" button to see the final price and total discount percentage.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                        <FaChartLine className="text-purple-500 dark:text-purple-400" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold">Visual Price Comparison</h3>
                      <p className="text-gray-600 dark:text-gray-400">See how each discount affects the price with our interactive line chart.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                        <FaUsers className="text-purple-500 dark:text-purple-400" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold">Real-time Calculations</h3>
                      <p className="text-gray-600 dark:text-gray-400">Get instant results as you input values.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                        <FaMoneyBill className="text-purple-500 dark:text-purple-400" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold">Accurate Results</h3>
                      <p className="text-gray-600 dark:text-gray-400">Calculate sequential discounts with precision, including intermediate prices and total savings.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4">FAQ</h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-purple-500 dark:border-purple-400 pl-4">
                    <h3 className="font-semibold">What is a double discount?</h3>
                    <p className="text-gray-600 dark:text-gray-400">A double discount is when two separate discounts are applied sequentially to a product's price. The second discount is applied to the price after the first discount has been deducted.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 dark:border-purple-400 pl-4">
                    <h3 className="font-semibold">Why use this calculator?</h3>
                    <p className="text-gray-600 dark:text-gray-400">This calculator helps you understand the true impact of sequential discounts and ensures you're getting the best deal possible. It's particularly useful for comparing different discount combinations.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 dark:border-purple-400 pl-4">
                    <h3 className="font-semibold">Can I use this for any currency?</h3>
                    <p className="text-gray-600 dark:text-gray-400">Yes, while the calculator shows results in ₹ (Indian Rupees), you can use it for any currency. Just input your values in your preferred currency.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 dark:border-purple-400 pl-4">
                    <h3 className="font-semibold">What if I only have one discount?</h3>
                    <p className="text-gray-600 dark:text-gray-400">You can still use this calculator! Just enter your single discount percentage in the "First Discount" field and leave the "Second Discount" field at 0%.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoubleDiscountCalculator;
